// CSS-as-string for injection into the iframe's <style>, NOT into document.head.
// Vite's `?inline` query returns the compiled CSS string instead of triggering
// the runtime style injection.
// eslint-disable-next-line import/no-unresolved
import storyContentStyles from '@opengov/stories-shared/styles/story-modules.css?inline';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { Text } from '@opengov/component-library/capital';
import { PROPOSAL_SUMMARY } from '@opengov/stories-shared/constants';
import { IPublication, IStory, IStoryContent } from '@opengov/stories-shared/types';
import { generateHTML, sortStoryContents, createContainer } from '@opengov/stories-shared/utils';
import React, { useMemo } from 'react';
import Frame, { FrameContextConsumer } from 'react-frame-component';
import {
  InactiveStoryMergeTagsType,
  MergeTagsOperation,
  StoryMergeTagsOperation
} from '../../common/context/MergeTagsContext';

import { ITinyMCEStoryEditorBaseProps } from '../../components/StoryEditor/StoryEditor/TinyMCEStoryEditor/TinyMCEStoryEditor';
import { TinyMCEStoryEditor } from '../../components/StoryEditor';
import { TINYMCE_TOOLBAR_CONTAINER_ID } from '../../components/StoryEditor/StoryEditor/TinyMCEStoryEditor/components/TinyMCEEditor';
import {
  InactiveStoryMergeTagsBanner,
  inactiveStoryMergeTagsBannerCss
} from '../../components/StoryView/InactiveStoryMergeTagsBanner';

import { updateStoryContentId } from '../dom';

import styles from './story.scss';
import { capitalMuiTheme } from '@opengov/capital-mui-theme';
import { ThemeProvider } from '@mui/material';
import { IModalMessages } from '../../../types';
import { useFetchBnpAiToggleState } from './hooks/bnpCommonService/useFetchBnpAiToggleState';
import { StoryLoadingSpinner } from '../../components/StoryEditor/StoryLoadingSpinner/StoryLoadingSpinner';
import { CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE } from '../../constants/modals';

// Mirror the parent frame's CSS into the editor iframe. The parent's CSS may
// be served as <style> tags (webpack dev with style-loader, MUI/emotion
// runtime) or as <link rel="stylesheet"> (Vite preview, webpack prod). The
// iframe needs both because ovid's @font-face for opengovicons-base lives in
// the app's main stylesheet, and without it the section-action icon glyphs
// don't render.
function generateStoryEditorCSS() {
  const styleElements = Array.from(document.getElementsByTagName('style'));
  const styleText = styleElements.reduce((styles, current) => styles + current.textContent, '');
  // Compute the allowed CDN origin once. Vite emits absolute CloudFront URLs
  // for non-dev builds (the parent page is on controlpanel.<env> but stylesheets
  // are on the CDN host). Read the CDN host from the env blob htmlInjectionPlugin
  // wrote into window.process.env so we can allowlist it for iframe mirroring.
  const cdnEnv = (window as unknown as { process?: { env?: Record<string, string | undefined> } })
    .process?.env;
  const cdnOrigin = (() => {
    const raw = cdnEnv?.CLOUD_FRONT_DOMAIN;
    if (!raw) return undefined;
    try {
      return new URL(raw).origin;
    } catch {
      return undefined;
    }
  })();

  const linkElements = Array.from<HTMLLinkElement>(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  ).filter((link) => {
    if (!link.href) return false;
    // Scheme allowlist before any other check. Browsers parse the resolved
    // .href on an HTMLLinkElement, so in practice `javascript:` / `data:` /
    // `vbscript:` URLs in a <link rel="stylesheet"> never load — but an
    // explicit guard keeps SAST satisfied and documents the invariant.
    let linkOrigin: string;
    try {
      const parsed = new URL(link.href);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
      linkOrigin = parsed.origin;
    } catch {
      return false;
    }
    // PSP mode: pspEntry.tsx injects the bundle's sibling style.css with a
    // data-stories-psp attribute. The link's origin is the module-load origin
    // (e.g. http://localhost:3003 in local PSP testing, a CloudFront host in
    // deployed PSP), which is intentionally cross-origin from
    // window.location.origin (psp.ogov.me). Match on the attribute so we
    // always copy our own bundle stylesheet into the iframe.
    if (link.getAttribute('data-stories-psp') === 'true') return true;
    // webpack prod emits styles.<hash>.stories.css?v=<stamp>. Keep this match
    // for back-compat with any deployed bundle still on the webpack pipeline.
    if (link.href.includes('stories.css?v')) return true;
    // Same-origin (local Vite preview, dev, anything served from the page's
    // own origin) OR our deploy's CloudFront CDN host (Vite-emitted prod
    // assets). Cross-origin stylesheets from any OTHER host are rejected so
    // a stray third-party <link> can't sneak into the editor iframe.
    if (linkOrigin === window.location.origin) return true;
    if (cdnOrigin && linkOrigin === cdnOrigin) return true;
    return false;
  });
  return (
    <>
      {styleText ? <style>{styleText}</style> : null}
      {linkElements.map((link) => {
        // sanitizeUrl returns 'about:blank' for any non-http(s) scheme
        // (javascript:, data:, vbscript:, etc.). We skip rendering in that
        // case rather than emit an inert <link href="about:blank">. The
        // upstream filter already rejects non-allowlisted origins; this is
        // defense-in-depth at the JSX attribute site for SAST.
        const safe = sanitizeUrl(link.href);
        if (safe === 'about:blank') return null;
        return <link key={safe} href={safe} rel="stylesheet" type="text/css" />;
      })}
    </>
  );
}

// Since the story is rendered in an iframe, our webpack build process does not
// automatically include the CSS files for any CDS components used as part of the story
// editor. To fix this, when in the editor, this script includes any
// stylesheets that are part of the parent frame in the iframe itself.
export function generateCSS(isReadOnly: boolean): React.ReactNode {
  return isReadOnly ? (
    <style>{storyContentStyles}</style>
  ) : (
    <>
      {generateStoryEditorCSS()}
      <style>{storyContentStyles}</style>

      <style>{inactiveStoryMergeTagsBannerCss}</style>
    </>
  );
}

export interface IStoryContentsFrameProps extends ITinyMCEStoryEditorBaseProps {
  storyTitle: string;
  storyDescription: string;
  isReadOnly: boolean;
  entitySubdomain: string;
  canAddProposalContent: boolean;
  publication?: IPublication;
  publicationId?: string;
  inactiveStoryMergeTagsType?: InactiveStoryMergeTagsType;
  modalMessages: IModalMessages;
  entityUuid: string;
  isAiToggleFeatureEnabled: boolean;
  isCustomSpreadsheetReportEnabled: boolean;
  updateAllStoryContentsMergeTagsDB?(props: {
    isTemplate: boolean;
    story: IStory;
    storyContents: IStoryContent[];
    publicationId: string;
    userId: number;
    storyMergeTagsOperation: StoryMergeTagsOperation;
  }): Promise<void>;
}

// Render the storyContents as editor components within an iframe
export const StoryContentsFrame = React.memo((props: IStoryContentsFrameProps) => {
  const {
    isTemplate,
    isReadOnly,
    entitySubdomain,
    storyTitle,
    storyDescription,
    story,
    storyContents,
    settings,
    canAddProposalContent,
    publication = null,
    userId = null,
    updateAllStoryContentsMergeTagsDB,
    inactiveStoryMergeTagsType,
    isAiToggleFeatureEnabled,
    ...restProps
  } = props;

  const {
    data: isAiEnabledByAdmin,
    isLoading: aiToggleLoading,
    isFetching: aiToggleFetching
  } = useFetchBnpAiToggleState({
    entityUuid: restProps.entityUuid,
    messages: restProps.messages,
    enabled: !isReadOnly && isAiToggleFeatureEnabled,
    isAiToggleFeatureEnabled
  });
  const isAiToggleStateLoading = aiToggleLoading && aiToggleFetching;
  // The 'nextStoryContentOrder' prop being passed down here refers to the order of the next storyContent
  // in sequence, and is used to tell the Story container where to add this storyContent.
  const { containers, updatedStoryContents } = createContainer(storyContents);
  const sortedStoryContents = useMemo(
    // Group the story contents by type and make sure they are ordered sequentially
    () => sortStoryContents(updatedStoryContents.map(updateStoryContentId)),
    [storyContents, updateStoryContentId]
  );
  const isStoryWithProgressTracker = sortedStoryContents.some(
    (storyContent) => Array.isArray(storyContent.contentChartConfig) && storyContent?.contentChartConfig.length > 0
  );
  const head = useMemo(() => {
    return generateCSS(isReadOnly);
  }, [isReadOnly]);
  const initialContent = useMemo(
    () =>
      generateHTML({
        id: story._id,
        title: storyTitle,
        description: storyDescription,
        fonts: [settings.brand.fonts],
        hasGallery: true,
        thumbnail: story.thumbnail || '',
        isReadOnly,
        storyCustomCss: story.customCssCode,
        publicationCustomCss: publication?.customCssCode || story.folder?.publication?.customCssCode,
        options: { gaTrackingCode: settings.advanced.google_analytics_id },
        isFollowable: story.isFollowable,
        containers,
        isStoryWithProgressTracker
      }),
    [story, storyTitle, storyDescription, settings, isReadOnly, publication]
  );
  const storiesWithReportTables = useMemo(
    () =>
      sortedStoryContents.filter(
        (storyContent: IStoryContent) =>
          storyContent?.content?.includes('data-media-type="tableTile"') ||
          storyContent?.contentHtml?.includes('data-media-type="tableTile"') ||
          storyContent?.content?.includes(`data-media-type="${CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE}"`) ||
          storyContent?.contentHtml?.includes(`data-media-type="${CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE}"`)
      ),
    [sortedStoryContents]
  );
  const StoryEditor = TinyMCEStoryEditor;

  if (isAiToggleStateLoading && !isReadOnly) {
    return <StoryLoadingSpinner />;
  }

  return (
    <Frame head={head} initialContent={initialContent} mountTarget="#wrapper">
      <FrameContextConsumer>
        {
          // Callback is invoked with iframe's window and document instances
          ({ document, window }: { document: Document; window: Window }) => {
            const cache = createCache({
              key: 'mui',
              container: document.head
            });
            return (
              <main>
                {sortedStoryContents.length > 0 ? (
                  <div id="story-content-root">
                    <div id={TINYMCE_TOOLBAR_CONTAINER_ID} className={styles.toolbarContainer} />
                    {!isReadOnly && canAddProposalContent ? (
                      <InactiveStoryMergeTagsBanner
                        document={document}
                        messages={restProps.messages}
                        type={inactiveStoryMergeTagsType}
                        onRemoveAllClick={() => {
                          return updateAllStoryContentsMergeTagsDB({
                            userId,
                            publicationId: publication?.id,
                            isTemplate,
                            story,
                            storyContents,
                            storyMergeTagsOperation: { operation: MergeTagsOperation.Remove, onlyInactive: true }
                          });
                        }}
                      />
                    ) : null}
                    {sortedStoryContents.map((storyContent, idx) => (
                      <CacheProvider key={`storyContent-${storyContent._id}`} value={cache}>
                        <ThemeProvider theme={capitalMuiTheme}>
                          <StoryEditor
                            {...restProps}
                            key={`storyContent-${storyContent._id}`}
                            totalReportTables={storiesWithReportTables.length}
                            isTemplate={isTemplate}
                            isReadOnlySection={storyContent.format === PROPOSAL_SUMMARY ? true : isReadOnly}
                            isReadOnlyMode={isReadOnly}
                            isLast={sortedStoryContents.length === idx + 1}
                            story={story}
                            storyContent={storyContent}
                            storyContents={sortedStoryContents}
                            totalStoryContents={sortedStoryContents.length}
                            nextStoryContentOrder={idx + 2}
                            settings={settings}
                            entitySubdomain={entitySubdomain}
                            document={document}
                            window={window}
                            publicationId={publication?.id ?? restProps.publicationId}
                            userId={userId}
                            canAddProposalContent={canAddProposalContent}
                            modalMessages={restProps.modalMessages}
                            isAiEnabledByAdmin={isAiEnabledByAdmin}
                          />
                        </ThemeProvider>
                      </CacheProvider>
                    ))}
                    {isReadOnly && (
                      <div className="powered-by">
                        <a
                          aria-label="Read more about OpenGov"
                          href="https://opengov.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Text color="white" size="minimum">
                            {'Powered By OpenGov'}
                          </Text>
                        </a>
                      </div>
                    )}
                  </div>
                ) : null}
              </main>
            );
          }
        }
      </FrameContextConsumer>
    </Frame>
  );
});

StoryContentsFrame.displayName = 'StoryContentsFrame';
